import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Loader2, FileText } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const Login = () => {
  const [loading, setLoading] = useState(false);
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(loginData.email, loginData.password);
      toast.success('Welcome back!');
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const downloadUserManual = () => {
    // Create a temporary link element to trigger download
    const link = document.createElement('a');
    link.href = `${API}/api/user-manual`;
    link.download = '911_Pro_Home_Live_User_Manual.pdf';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="login-bg min-h-screen flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60" />
      
      <Card className="relative w-full max-w-md bg-white/95 backdrop-blur-md border-0 shadow-2xl rounded-sm" data-testid="login-card">
        <CardHeader className="text-center pb-2">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-12 h-12 bg-red-600 rounded-sm flex items-center justify-center">
              <span className="text-white font-black text-xl">911</span>
            </div>
          </div>
          <CardTitle className="text-2xl font-black text-slate-900 tracking-tight">
            PRO HOME LIVE
          </CardTitle>
          <CardDescription className="text-slate-600">
            INVENTORY MANAGEMENT SYSTEM
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="login-email">Email</Label>
              <Input
                id="login-email"
                type="email"
                placeholder="you@company.com"
                value={loginData.email}
                onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                required
                className="rounded-sm"
                data-testid="login-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="login-password">Password</Label>
              <Input
                id="login-password"
                type="password"
                placeholder="••••••••"
                value={loginData.password}
                onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                required
                className="rounded-sm"
                data-testid="login-password-input"
              />
            </div>
            <Button
              type="submit"
              className="w-full bg-slate-900 hover:bg-slate-800 rounded-sm"
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Sign In
            </Button>
          </form>
          
          <div className="mt-6 pt-4 border-t border-slate-200">
            <Button
              type="button"
              variant="outline"
              className="w-full rounded-sm"
              onClick={downloadUserManual}
              data-testid="download-manual-btn"
            >
              <FileText className="mr-2 h-4 w-4" />
              Download User Manual (PDF)
            </Button>
          </div>
          
          <p className="text-xs text-slate-500 text-center mt-4">
            Contact your administrator for account access
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default Login;
